import { useCallback, useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { makeForm } from "@/lib/upload";
import { toast } from "sonner";
import { fmtDate, fmtCurrency } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { FileSignature, Loader2, Download, RefreshCw, Pencil, Check, X } from "lucide-react";
import { AssinajaDialog } from "@/components/AssinajaDialog";

const card = "bg-white rounded-[18px] p-[18px] md:p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100";
const STATE = {
  ok: "text-slate-800",
  falta: "text-slate-400 italic",
  confirmar: "text-amber-700",
  vazio: "text-slate-300 italic",
};

const DATA_GAPS = ["preço", "comissão", "exclusividade"];
const FIELD_KEY = { "preço": "preco", "comissão": "comissao", exclusividade: "exclusividade" };

const openBlob = async (url, name) => {
  const res = await api.get(url, { responseType: "blob" });
  const href = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = href; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(href), 4000);
};

function statusText(s) {
  const items = s.items || [];
  const st = s.versions?.[0]?.sign?.status;
  if (st === "enviado_assinaja") return "Enviado para assinatura · a aguardar assinaturas";
  if (items.length) return `Para o CMI · ${s.count_text || (items.length === 1 ? "falta 1" : `faltam ${items.length}`)}`;
  if (st === "falhou") return "Assinatura digital não concluída";
  if (s.versions?.length) return "Gerado · por assinar";
  return "Pronto a gerar · todos os dados preenchidos";
}

export const cmiSigned = (p) => {
  const d = (p.documents || []).find((x) => x.key === "contrato_mediacao");
  return !!(p.mediation?.signed_date && d?.status === "recebido");
};

function signedLine(p) {
  const m = p.mediation || {};
  const comm = m.commission_value == null ? null : m.commission_type === "valor" ? fmtCurrency(m.commission_value) : `${m.commission_value}%`;
  const end = p.mediation_end && `${p.mediation_renews ? "renova-se a" : "termina a"} ${fmtDate(p.mediation_end)}`;
  return [`CMI assinado a ${fmtDate(m.signed_date)}`, m.exclusive == null ? null : m.exclusive ? "Exclusivo" : "Não exclusivo", comm, end].filter(Boolean).join(" · ");
}

export function CmiCard({ pid, p, readOnly, showSteps, onAsk, hideTitle = true }) {
  const [s, setS] = useState(null);
  const [open, setOpen] = useState(false);
  const [more, setMore] = useState(false);
  const load = useCallback(() => api.get(`/processes/${pid}/cmi`).then(({ data }) => setS(data)).catch(() => setS(null)), [pid]);
  useEffect(() => { load(); }, [load]);
  const signed = p && cmiSigned(p);
  if (!s?.enabled && !signed) return null;
  const last = s?.versions?.[0];
  const docGaps = (s?.items || []).some((g) => !DATA_GAPS.includes(g));
  if (signed && (!more || !s?.enabled)) {
    return (
      <div className={`${card} !py-4 flex items-center gap-3`} data-testid="cmi-card">
        <FileSignature size={17} className="text-ec-green shrink-0" />
        <span className="text-sm font-medium text-ec-green flex-1 min-w-0" data-testid="cmi-signed-summary">{signedLine(p)}</span>
        {s?.enabled && <button onClick={() => setMore(true)} className="text-xs font-semibold text-slate-500 hover:text-ec-green shrink-0" data-testid="cmi-signed-more">Ver CMI</button>}
      </div>
    );
  }
  return (
    <div className={card} data-testid="cmi-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          {!hideTitle && <div className="flex items-center gap-2 font-heading text-lg text-ec-green"><FileSignature size={18} /> Contrato de mediação</div>}
          {s.blocked && <p className="text-sm font-semibold text-red-700 mt-1" data-testid="cmi-blocked">CMI bloqueado · {s.blocked}</p>}
          <p className={`text-sm mt-1 ${s.ready ? "text-emerald-700" : "text-amber-700"}`} data-testid="cmi-status">{statusText(s)}</p>
          {(s.items || []).length > 0 && (
            <ul className="text-xs text-slate-600 mt-1 space-y-0.5" data-testid="cmi-gaps">
              {s.items.map((g, i) => {
                const l = s.item_links?.[i];
                const go = readOnly ? null : l?.field ? () => setOpen(FIELD_KEY[l.key] || l.key) : onAsk;
                return <li key={g}>{go ? <button type="button" onClick={go} className="underline underline-offset-2 hover:text-ec-green text-left" data-testid={`cmi-gap-${i}`}>{l?.field ? `Preencher: ${g.split(":")[0]}` : g}</button> : g}</li>;
              })}
            </ul>
          )}
          {s.confirm > 0 && <p className="text-xs text-amber-700 mt-0.5" data-testid="cmi-confirm-count">{s.confirm} {s.confirm === 1 ? "dado a confirmar" : "dados a confirmar"}</p>}
          {last && <p className="text-xs text-slate-500 mt-1">Última versão: v{last.n} · {new Date(last.at).toLocaleDateString("pt-PT")} · {last.by}</p>}
          {s.stale && !s.blocked && <p className="text-xs font-semibold text-sky-700 mt-1" data-testid="cmi-stale">CMI desatualizado · gerar de novo</p>}
          {(s.areas || []).map((a) => <AreaAck key={a.id} pid={pid} a={a} readOnly={readOnly} onDone={load} />)}
        </div>
        <div className="flex items-center gap-3">
          {signed && <button onClick={() => setMore(false)} className="text-xs font-semibold text-slate-500 hover:text-ec-green" data-testid="cmi-signed-less">Fechar</button>}
          {!readOnly && (s.blocked
            ? <button disabled className="inline-flex items-center gap-1.5 bg-slate-200 text-slate-500 px-4 py-2 rounded-lg text-sm font-semibold cursor-not-allowed" data-testid="cmi-blocked-btn">{s.blocked}</button>
            : docGaps && !last && onAsk
            ? <button onClick={onAsk} data-testid="cmi-ask-docs-btn"
                className="inline-flex items-center gap-1.5 bg-ec-green text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-ec-greenDark transition-colors">
                Pedir documentos
              </button>
            : <button onClick={() => setOpen(true)} data-testid="cmi-open-btn"
                className="inline-flex items-center gap-1.5 bg-ec-green text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-ec-greenDark transition-colors">
                {last ? "Rever CMI" : (s.items || []).length ? "Completar dados do CMI" : "Ver e gerar CMI"}
              </button>)}
        </div>
      </div>
      {open && <CmiReview pid={pid} s={s} reload={load} focus={typeof open === "string" ? open : null} onClose={() => setOpen(false)} />}
      {last && !signed && !readOnly && <CmiSign pid={pid} p={p} last={last} open={showSteps} reload={load} s={s} />}
    </div>
  );
}

const SEND_LOCK = "Gere uma versão atualizada e completa antes de enviar.";

// Certidão vs caderneta com mais de 10%: o consultor confirma com uma nota e o CMI avança; a diferença continua como tarefa.
function AreaAck({ pid, a, readOnly, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const tid = a.id.split(":").pop();
  const save = async () => {
    setBusy(true);
    try { await api.post(`/processes/${pid}/area-ack`, { check_id: a.id, note }); toast.success("Diferença registada · o CMI pode avançar"); onDone(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 max-w-xl" data-testid={`cmi-area-${tid}`}>
      <p className="text-sm font-semibold text-amber-900">{a.title}</p>
      <p className="text-xs text-amber-800 mt-0.5">{a.a?.label}: {a.a?.value} · {a.b?.label}: {a.b?.value}</p>
      {a.ack ? (
        <p className="text-xs text-slate-600 mt-1" data-testid={`cmi-area-ack-${tid}`}>Visto por {a.ack.by}: «{a.ack.note}» · fica como tarefa até corrigir ou justificar</p>
      ) : !readOnly && (
        <div className="flex gap-2 mt-2">
          <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} placeholder="Nota curta (ex.: marquise não legalizada)" className="h-9 bg-white text-sm" data-testid={`cmi-area-note-${tid}`} />
          <button onClick={save} disabled={busy || note.trim().length < 3} className="h-9 px-3 rounded-lg bg-slate-900 text-white text-xs font-semibold whitespace-nowrap disabled:opacity-50" data-testid={`cmi-area-ok-${tid}`}>Vi a diferença</button>
        </div>
      )}
    </div>
  );
}

function sendLock(s) {
  if (s.blocked || (s.items || []).length || s.stale) return SEND_LOCK;
  if (!s.esign?.enabled) return "A opção «Assinatura digital (AssinaJá)» está desligada nas Definições.";
  return null;
}

function SignState({ sign }) {
  const list = sign.signers || [];
  const done = list.filter((x) => x.status === "assinado").length;
  if (sign.status === "falhou") {
    return <div className="text-xs text-red-700 mt-1 font-semibold" data-testid="cmi-sign-failed">{sign.fail_reason} Reveja e envie de novo.</div>;
  }
  if (sign.status === "assinado") return <div className="text-xs text-emerald-700 mt-1 font-semibold" data-testid="cmi-sign-done">Assinado por todos.</div>;
  return (
    <div className="mt-1" data-testid="cmi-sign-waiting">
      <div className="text-xs font-semibold text-amber-800" data-testid="cmi-sign-count">A aguardar assinaturas · {done} de {list.length}</div>
      <div className="text-xs text-slate-600 mt-0.5" data-testid="cmi-sign-missing">Falta: {list.filter((x) => x.status !== "assinado").map((x) => x.name).join(", ")}</div>
    </div>
  );
}

// CMI gerado e por assinar: descarregar → enviar ao cliente → carregar o assinado.
function CmiSign({ pid, p, last, open, reload, s }) {
  const ref = useRef(null);
  const [show, setShow] = useState(() => open || window.location.hash === "#sec-cmi");
  const [sign, setSign] = useState(false);
  const st = last.sign?.status;
  const waiting = st === "enviado_assinaja";
  const lock = sendLock(s);
  useEffect(() => {
    if (!waiting) { return undefined; }
    const t = setInterval(reload, 10000);
    return () => clearInterval(t);
  }, [waiting, reload]);
  useEffect(() => {
    const k = `cmi-signed-${pid}-${last.n}`;
    if (st === "assinado" && !sessionStorage.getItem(k)) { sessionStorage.setItem(k, "1"); window.location.reload(); }
  }, [st, pid, last.n]);
  const get = () => openBlob(`/processes/${pid}/cmi/versions/${last.n}`, last.filename);
  const upload = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    try { await api.post(`/processes/${pid}/documents/contrato_mediacao/upload`, await makeForm([["file", f]])); toast.success("CMI assinado carregado"); window.location.reload(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail) || "Falha no carregamento"); }
  };
  const b = "h-9 whitespace-nowrap inline-flex items-center gap-2 px-3 rounded-lg text-sm font-semibold border border-slate-300 bg-white text-slate-800 hover:bg-slate-50";
  const box = (on) => `rounded-xl p-3.5 border ${on ? "border-slate-800 border-2" : "border-slate-200"}`;
  if (!show) return <div className="mt-4"><button onClick={() => setShow(true)} className={b} data-testid="cmi-view">Ver CMI</button></div>;
  return (
    <div className="mt-4 grid sm:grid-cols-3 gap-2.5" data-testid="cmi-sign-steps">
      {s.stale ? (
        <div className="rounded-xl p-3.5 border border-amber-200 bg-amber-50/60" data-testid="cmi-step1">
          <div className="text-sm font-semibold text-amber-800">1. Desatualizado · gerar de novo</div>
          <div className="text-xs text-slate-600 mt-0.5">Os dados mudaram desde a v{last.n}. Abra «Rever CMI» e gere outra versão.</div>
        </div>
      ) : (
        <div className="rounded-xl p-3.5 border border-emerald-200 bg-emerald-50/50" data-testid="cmi-step1">
          <div className="text-sm font-semibold navy-text">✓ 1. Gerado</div>
          <div className="text-xs text-slate-600 mt-0.5">Preenchido com os documentos lidos (v{last.n}).</div>
        </div>
      )}
      <div className={box(true)}>
        <div className="flex items-center gap-2 flex-wrap"><div className="text-sm font-semibold navy-text">2. Assinar</div>
          {waiting && <span className="text-xs font-semibold px-2 py-0.5 rounded-md bg-amber-50 text-amber-800" data-testid="cmi-sign-badge">A aguardar assinaturas</span>}</div>
        {last.sign?.signers ? <SignState sign={last.sign} /> : <div className="text-xs text-slate-600 mt-0.5">Envie para assinatura digital ou descarregue para imprimir.</div>}
        <div className="flex flex-wrap gap-1.5 mt-2">
          {waiting
            ? <button onClick={() => setSign(true)} className={`${b} !bg-ec-green !text-white !border-transparent`} data-testid="cmi-send-assinaja"><FileSignature size={14} />Ver estado</button>
            : <button onClick={() => setSign(true)} disabled={!!lock} className={`${b} !bg-ec-green !text-white !border-transparent disabled:opacity-50 disabled:cursor-not-allowed`} data-testid="cmi-send-assinaja"><FileSignature size={14} />{st === "falhou" ? "Enviar de novo" : "Enviar para assinatura"}</button>}
          <button onClick={get} className={b} data-testid="cmi-download"><Download size={14} />Descarregar</button>
        </div>
        {!waiting && lock && <p className="text-xs text-slate-500 mt-1.5" data-testid="cmi-send-lock">{lock}</p>}
        {sign && <AssinajaDialog version={last} onClose={() => setSign(false)} onSent={() => { setSign(false); reload(); }} />}
      </div>
      <div className={box(false)}>
        <div className="text-sm font-semibold navy-text" data-testid="cmi-step3">{st === "assinado" ? "✓ 3. Assinado" : "3. Carregar assinado"}</div>
        <div className="text-xs text-slate-600 mt-0.5">Fotografe ou envie o PDF.</div>
        <button onClick={() => ref.current?.click()} className={`${b} mt-2`} data-testid="cmi-upload-signed">Carregar CMI assinado</button>
        <input ref={ref} type="file" accept="application/pdf,image/*" className="hidden" onChange={upload} data-testid="cmi-upload-input" />
      </div>
    </div>
  );
}

function CmiReview({ pid, s, reload, onClose, focus }) {
  const [pages, setPages] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!focus) { return undefined; }
    const t = setTimeout(() => {
      document.querySelector(`[data-testid=cmi-field-${focus}]`)?.scrollIntoView({ block: "center" });
      document.querySelector(`[data-testid=cmi-edit-${focus}]`)?.click();
    }, 300);
    return () => clearTimeout(t);
  }, [focus]);
  const refreshPdf = useCallback(async () => {
    const res = await Promise.allSettled([0, 1, 2, 3, 4, 5].map((i) => api.get(`/processes/${pid}/cmi/preview`, { params: { page: i, t: Date.now() }, responseType: "blob" })
      .then((r) => URL.createObjectURL(r.data))));
    const urls = res.filter((r) => r.status === "fulfilled").map((r) => r.value);
    setPages((old) => { (old || []).forEach((u) => URL.revokeObjectURL(u)); return urls; });
  }, [pid]);
  useEffect(() => { refreshPdf().catch(() => {}); }, [refreshPdf]);
  const changed = async () => { await reload(); refreshPdf().catch(() => {}); };
  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/processes/${pid}/cmi/generate`);
      toast.success(data.missing ? `CMI v${data.n} gerado · ${data.missing} campos em branco` : `CMI v${data.n} gerado`);
      await reload();
      openBlob(`/processes/${pid}/cmi/versions/${data.n}`, data.filename);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const groups = [...new Set(s.fields.map((f) => f.group))];
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[1200px] w-[96vw] h-[90vh] flex flex-col" data-testid="cmi-review">
        <DialogHeader>
          <DialogTitle className="font-heading text-ec-green">Rever CMI</DialogTitle>
          <DialogDescription>Só se preenchem os espaços em branco do modelo. O texto das cláusulas nunca é alterado.</DialogDescription>
        </DialogHeader>
        <div className="grid lg:grid-cols-[1fr_1fr] gap-5 flex-1 min-h-0">
          <div className="overflow-y-auto pr-2 space-y-5" data-testid="cmi-fields">
            {s.sellers_over && <p className="text-sm text-amber-700">O modelo tem lugar para 2 proprietários: os restantes não entram no CMI.</p>}
            {groups.map((g) => (
              <div key={g}>
                <div className="text-xs font-semibold tracking-[0.12em] text-slate-500 uppercase mb-1.5">{g}</div>
                <div className="divide-y divide-slate-100 border border-slate-100 rounded-xl">
                  {s.fields.filter((f) => f.group === g).map((f) => <FieldRow key={f.key} pid={pid} f={f} onChange={changed} />)}
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-col min-h-0 gap-3">
            {pages ? (
              <div className="flex-1 overflow-y-auto rounded-xl border border-slate-200 bg-slate-100 p-3 space-y-3" data-testid="cmi-preview">
                {pages.map((u, i) => <img key={u} src={u} alt={`Página ${i + 1} do CMI`} className="w-full bg-white shadow-sm" data-testid={`cmi-preview-page-${i}`} />)}
              </div>
            ) : <div className="flex-1 grid place-items-center text-sm text-slate-400"><Loader2 className="animate-spin" /></div>}
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <Versions pid={pid} versions={s.versions} />
              <button onClick={generate} disabled={busy} data-testid="cmi-generate-btn"
                className="inline-flex items-center gap-2 bg-ec-green text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-ec-greenDark disabled:opacity-60">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
                {s.missing.length ? `Gerar CMI · ${s.missing.length} em branco` : "Gerar CMI"}
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FieldRow({ pid, f, onChange }) {
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState(f.value || "");
  const save = async (value) => {
    try { await api.put(`/processes/${pid}/cmi/field`, { key: f.key, value }); setEdit(false); onChange(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const label = f.state === "falta" ? "Falta" : f.state === "vazio" ? "—" : f.value;
  return (
    <div className="px-3 py-2 flex items-start gap-3" data-testid={`cmi-field-${f.key}`}>
      <div className="w-44 shrink-0 text-xs text-slate-500 pt-1">{f.label}</div>
      <div className="flex-1 min-w-0">
        {edit ? (
          <div className="flex items-center gap-1.5">
            <Input value={val} onChange={(e) => setVal(e.target.value)} className="h-8 text-sm" autoFocus data-testid={`cmi-input-${f.key}`} />
            <button onClick={() => save(val)} className="p-1.5 rounded hover:bg-emerald-50 text-emerald-700" data-testid={`cmi-save-${f.key}`}><Check size={15} /></button>
            <button onClick={() => setEdit(false)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500"><X size={15} /></button>
          </div>
        ) : (
          <>
            <div className={`text-sm break-words ${STATE[f.state]}`} data-testid={`cmi-value-${f.key}`}>
              {f.state === "confirmar" && <span className="inline-block mr-1.5 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[11px] not-italic font-semibold">Confirmar</span>}
              {label}
            </div>
            {f.note && <div className="text-[11px] text-amber-700 mt-0.5">{f.note}</div>}
            {f.source && <div className="text-[11px] text-slate-400 mt-0.5">{f.source}</div>}
          </>
        )}
      </div>
      {!edit && f.key !== "data" && (
        <button onClick={() => { setVal(f.value || ""); setEdit(true); }} className="p-1.5 rounded text-slate-400 hover:text-ec-green hover:bg-slate-50" data-testid={`cmi-edit-${f.key}`}><Pencil size={13} /></button>
      )}
    </div>
  );
}

function Versions({ pid, versions }) {
  if (!versions.length) return <span className="text-xs text-slate-400">Ainda sem versões geradas.</span>;
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs" data-testid="cmi-versions">
      <span className="text-slate-500">Versões:</span>
      {versions.map((v) => (
        <button key={v.n} onClick={() => openBlob(`/processes/${pid}/cmi/versions/${v.n}`, v.filename)} data-testid={`cmi-version-${v.n}`}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-200 hover:bg-slate-50" title={`${v.by} · ${new Date(v.at).toLocaleString("pt-PT")}`}>
          <Download size={11} /> v{v.n}
        </button>
      ))}
    </div>
  );
}
